import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Box, ButtonBase } from "@mui/material";
import { FormikHelpers } from "formik";
import { useSelector } from "react-redux";

import blankProfilePhoto from "../../../../assets/blank-profile-picture.jpg";
import { useAdminProviderDataContext } from "../../../../context";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

interface IFilePreview extends File {
  preview: string;
}
interface IProps<TSetFieldValue> {
  setFieldValue: FormikHelpers<TSetFieldValue>["setFieldValue"];
  fieldName: keyof TSetFieldValue & string;
  backgroundImageUrl: string | null;
  maxFiles?: number;
  accept?: string;
}
const ProfilePhoto = <TSetFieldValue,>({
  setFieldValue,
  fieldName,
  backgroundImageUrl,
  maxFiles = 1,
  accept = "image/*",
}: IProps<TSetFieldValue>): React.ReactElement => {
  const { user } = useAdminProviderDataContext();
  const agent = useSelector((state) => state.agent);

  const isAllowedChangeProfilePhoto = user.position === "agent" && user.id !== agent.data.id;

  const [files, setFiles] = useState<IFilePreview[]>([]);
  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    disabled: isAllowedChangeProfilePhoto,
    maxFiles,
    maxSize: MAX_FILE_SIZE,
    accept: { [accept]: [] },
    onDrop: (acceptedFiles) => {
      const formData = new FormData();
      setFiles(
        acceptedFiles.map((file) => {
          formData.append("file", file);
          setFieldValue(fieldName, formData);
          return Object.assign(file, {
            preview: URL.createObjectURL(file),
          });
        })
      );
    },
  });

  const thumbs = files.map((file) => (
    <div key={file.name}>
      <div>
        <Box
          component="img"
          width="100%"
          alt="profile_photo"
          src={file.preview}
          onLoad={() => URL.revokeObjectURL(file.preview)}
        />
      </div>
    </div>
  ));

  const ValidateError = (): React.ReactElement | null => {
    if (fileRejections.length > 0) {
      const { code } = fileRejections[0].errors[0];
      const isFileTooLarge = code === "file-too-large";
      const errorMessage = isFileTooLarge
        ? "File is larger then 5MB"
        : fileRejections[0].errors[0].message;
      return (
        <Box
          sx={{
            padding: 5,
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "rgba(0,0,0,0.9)",
            cursor: "default",
          }}
        >
          <Box color="error">{errorMessage}</Box>
        </Box>
      );
    }
    return null;
  };

  useEffect(() => {
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, []);

  return (
    <ButtonBase
      sx={{
        width: "100%",
        height: "100%",
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        position: "relative",
        cursor: "pointer",
        ...(!thumbs.length && {
          backgroundImage: backgroundImageUrl
            ? `url('${backgroundImageUrl}')`
            : `url(${blankProfilePhoto})`,
        }),
      }}
    >
      {backgroundImageUrl && (
        <img
          alt="profile_photo"
          style={{ display: "none" }}
          referrerPolicy="no-referrer"
          src={backgroundImageUrl}
        />
      )}

      <Box width="100%" height="100%" {...getRootProps()}>
        <input {...getInputProps()} />
        <aside>{thumbs}</aside>
      </Box>
      <ValidateError />
    </ButtonBase>
  );
};

export default ProfilePhoto;
